import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import { FlashcardGenerator } from "./components/FlashcardGenerator";
import { FlashcardViewer } from "./components/FlashcardViewer";
import { RecallGenerator } from "./components/RecallGenerator";
import { RecallViewer } from "./components/RecallViewer";
import { AuthModal } from "./components/AuthModal";
import { Dashboard } from "./components/Dashboard";
import { ComingSoon } from "./components/ComingSoon";
import { HowToUse } from "./components/HowToUse";
import { useAuth } from "./context/AuthContext";
import { FlashcardCollection, RecallCollection, MapCollection } from "./types";
import { saveToLocal, loadFromLocal, STORAGE_KEY, exportAllData, importDataFromFile, saveAtlasToIndexedDB, AppData } from "./utils/storageUtils";
import { saveToIndexedDB, loadFromIndexedDB } from "./utils/indexedDb";
import { Profile } from "./components/Profile";
import { AtlasPage } from "./pages/AtlasPage";
import { MapViewer } from "./components/MapViewer";
import { AboutUs } from "./components/AboutUs";
import "./styles_1/App.css";

const ENABLE_FLASHCARD_GENERATOR = true;

// --- HELPER WRAPPER: FLASHCARD VIEWER ---
const FlashcardViewerRoute: React.FC<{
  collections: FlashcardCollection[];
  onUpdate: (updatedCol: FlashcardCollection) => void;
}> = ({ collections, onUpdate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const collection = collections.find((c) => c.id === id);

  if (!collection) return <Navigate to="/" />;

  // Safety check for empty collection
  if (!collection.flashcards || collection.flashcards.length === 0) {
    return (
      <div className="generator" style={{ textAlign: 'center', padding: '100px' }}>
        <h2>No cards in this deck</h2>
        <button onClick={() => navigate(-1)} className="secondary-btn">Go Back</button>
      </div>
    );
  }

  return (
    <FlashcardViewer
      collection={collection}
      onBack={() => navigate(-1)}
      onUpdateCollection={onUpdate}
    />
  );
};

// --- HELPER WRAPPER: RECALL VIEWER (UPDATED) ---
const RecallViewerRoute: React.FC<{
  sheets: RecallCollection[];
  onUpdate: (updatedSheet: RecallCollection) => void;
}> = ({ sheets, onUpdate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sheet = sheets.find((s) => s.id === id);

  if (!sheet) return <Navigate to="/" />;

  return (
    <RecallViewer
      sheet={sheet}
      onBack={() => navigate(-1)}
      onUpdate={onUpdate}
    />
  );
};

// --- HELPER WRAPPER: MAP VIEWER ---
const MapViewerRoute: React.FC<{
  sessions: MapCollection[];
  onUpdate: (updatedSession: MapCollection) => void;
}> = ({ sessions, onUpdate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = sessions.find((s) => s.id === id);

  if (!session) return <Navigate to="/" />;

  return (
    <MapViewer
      session={session}
      onBack={() => navigate(-1)}
      onUpdate={onUpdate}
    />
  );
};

const App: React.FC = () => {
  const [collections, setCollections] = useState<FlashcardCollection[]>([]);
  const [recallSheets, setRecallSheets] = useState<RecallCollection[]>([]);
  const [mapSessions, setMapSessions] = useState<MapCollection[]>([]);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // FIXED: Added 'logout' here so we can pass it to the Profile
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const initData = async () => {
      try {
        // 1. Try loading from IndexedDB
        let data = await loadFromIndexedDB();

        // 2. Migration: If no IDB data, check new localStorage then legacy localStorage
        if (!data) {
          const mainData = loadFromLocal();
          if (mainData) {
            data = mainData;
          } else {
            // Check legacy key
            const legacyRaw = localStorage.getItem("flashgenius_user_data");
            if (legacyRaw) {
              try {
                data = JSON.parse(legacyRaw);
                console.log("Migrated legacy FlashGenius data to IndexedDB");
              } catch (e) { console.error("Legacy parse failed", e); }
            }
          }

          if (data) {
            await saveToIndexedDB(data);
          }
        }

        if (data) {
          setCollections(data.collections || []);
          setRecallSheets(data.recallSheets || []);
          setMapSessions((data as AppData).mapSessions || []);
        }
      } catch (e) {
        console.error("Storage initialization failed", e);
      } finally {
        setIsLoading(false);
      }
    };
    initData();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      saveToIndexedDB({
        collections,
        recallSheets,
        mapSessions,
        lastUpdated: new Date().toISOString()
      });
    }
  }, [collections, recallSheets, mapSessions, isLoading]);

  const handleSaveCollection = (newCollection: FlashcardCollection) => {
    setCollections((prev) => [newCollection, ...prev]);
    navigate("/");
  };

  const handleSaveRecallSheet = (newSheet: RecallCollection) => {
    setRecallSheets((prev) => [newSheet, ...prev]);
    navigate("/");
  };

  const handleUpdateRecallSheet = (updatedSheet: RecallCollection) => {
    setRecallSheets((prev) =>
      prev.map((sheet) => (sheet.id === updatedSheet.id ? updatedSheet : sheet))
    );
  };

  const handleUpdateMapSession = (updatedSession: MapCollection) => {
    setMapSessions((prev) =>
      prev.map((session) => (session.id === updatedSession.id ? updatedSession : session))
    );
  };

  const handleImportData = (
    newCols: FlashcardCollection[],
    newSheets: RecallCollection[]
  ) => {
    setCollections(newCols);
    setRecallSheets(newSheets);
  };

  const handleClearData = () => {
    setCollections([]);
    setRecallSheets([]);
    localStorage.clear();
  };

  const handleDeleteCollection = (id: string) => {
    setCollections((prev) => prev.filter((c) => c.id !== id));
  };

  const handleUpdateCollection = (updatedCol: FlashcardCollection) => {
    setCollections((prev) =>
      prev.map((c) => (c.id === updatedCol.id ? updatedCol : c))
    );
  };

  const handleDeleteRecall = (id: string) => {
    setRecallSheets((prev) => prev.filter((s) => s.id !== id));
  };
  const handleDeleteMap = (id: string) => {
    setMapSessions((prev) => prev.filter((m) => m.id !== id));
  };

  const handleGlobalImport = async (file: File) => {
    try {
      const data: any = await importDataFromFile(file);

      // 1. Update React components
      if (data.collections) setCollections(data.collections);
      if (data.recallSheets) setRecallSheets(data.recallSheets);

      // 2. Handle Atlas data if present
      if (data.atlas) {
        await saveAtlasToIndexedDB(data.atlas);
        // Notify Atlas iframe if it's currently open
        const iframe = document.querySelector('iframe');
        iframe?.contentWindow?.postMessage({ type: 'GLOBAL_DATA_IMPORTED' }, '*');
      }

      alert("Data Imported Successfully! All your progress has been restored.");
      navigate("/");
    } catch (e: any) {
      alert("Import Failed: " + e.message);
    }
  };

  // NEW: Handle Logout logic
  const handleLogout = async () => {
    await logout();
    navigate("/"); // Redirect to home after logout
  };

  const handleSaveMap = (session: MapCollection) => {
    if (!user) {
      setIsAuthOpen(true);
      return;
    }
    setMapSessions(prev => [session, ...prev]);
    navigate("/");
  };

  const [isMainSidebarHidden, setIsMainSidebarHidden] = useState(false);

  const MainLayout: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    const location = useLocation();
    const isAtlas = location.pathname === "/atlas";

    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'TOGGLE_MAIN_SIDEBAR') {
          setIsMainSidebarHidden(prev => {
            const newState = !prev;
            const iframe = document.querySelector('iframe');
            iframe?.contentWindow?.postMessage({ type: 'MAIN_SIDEBAR_STATE_CHANGED', hidden: newState }, '*');
            return newState;
          });
        } else if (event.data.type === 'SET_MAIN_SIDEBAR') {
          setIsMainSidebarHidden(event.data.hidden);
          const iframe = document.querySelector('iframe');
          iframe?.contentWindow?.postMessage({ type: 'MAIN_SIDEBAR_STATE_CHANGED', hidden: event.data.hidden }, '*');
        } else if (event.data.type === 'SAVE_MAP_TO_DASHBOARD') {
          if (!user) {
            setIsAuthOpen(true);
          } else {
            handleSaveMap(event.data.session);
          }
        }
      };
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, []);

    // RESTORED & GRADUAL: Restore sidebar when navigating away from Atlas, hide when entering
    useEffect(() => {
      setIsMainSidebarHidden(isAtlas);
    }, [isAtlas]);

    if (isLoading) {
      return (
        <div className="app-loader" style={{
          height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc'
        }}>
          <div className="loader-spinner"></div>
          <p style={{ marginLeft: '15px', color: '#64748b', fontWeight: 600 }}>Loading ScoreSmart DB...</p>
        </div>
      );
    }

    return (
      <div className={`app ${isMainSidebarHidden ? 'hide-sidebar' : ''}`}>
        <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
        {!isMainSidebarHidden && (
          <Sidebar
            onLogin={() => setIsAuthOpen(true)}
            onLogout={handleLogout}
            onExport={() => exportAllData(collections, recallSheets, mapSessions)}
            onImport={handleGlobalImport}
            user={user}
          />
        )}
        <main
          className={isAtlas ? "atlas-main" : "main-content"}
        >
          {children}
        </main>
      </div>
    );
  };

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <MainLayout>
              <Dashboard
                collections={collections}
                recallSheets={recallSheets}
                mapSessions={mapSessions}
                onViewCollection={(col) => navigate(`/collection/${col.id}`)}
                onViewRecall={(sheet) => navigate(`/recall-sheet/${sheet.id}`)}
                onViewMap={(map) => navigate(`/map-viewer/${map.id}`)}
                onDeleteCollection={handleDeleteCollection}
                onDeleteRecall={handleDeleteRecall}
                onDeleteMap={handleDeleteMap}
                user={user}
                onShowAuth={() => setIsAuthOpen(true)}
              />
            </MainLayout>
          }
        />

        <Route
          path="/generator"
          element={
            <MainLayout>
              {ENABLE_FLASHCARD_GENERATOR ? (
                <FlashcardGenerator
                  onSaveCollection={handleSaveCollection}
                  user={user}
                  onShowAuth={() => setIsAuthOpen(true)}
                />
              ) : (
                <ComingSoon />
              )}
            </MainLayout>
          }
        />

        <Route
          path="/recall"
          element={
            <MainLayout>
              <RecallGenerator
                onSaveSheet={handleSaveRecallSheet}
                user={user}
                onShowAuth={() => setIsAuthOpen(true)}
              />
            </MainLayout>
          }
        />

        <Route
          path="/profile"
          element={
            <MainLayout>
              {/* FIXED: Passed all required props to Profile */}
              <Profile
                // Auth Props
                user={user}
                onLogout={handleLogout}
                onClose={() => navigate("/")} // Closing profile goes back to Dashboard

                // Data Props
                collections={collections}
                recallSheets={recallSheets}
                onImportData={handleImportData}
                onClearData={handleClearData}
              />
            </MainLayout>
          }
        />

        <Route
          path="/atlas"
          element={
            <MainLayout>
              <AtlasPage onShowAuth={() => setIsAuthOpen(true)} />
            </MainLayout>
          }
        />

        <Route
          path="/how-to"
          element={
            <MainLayout>
              <HowToUse onNavigateToGenerator={() => navigate("/generator")} />
            </MainLayout>
          }
        />

        <Route
          path="/about"
          element={
            <MainLayout>
              <AboutUs />
            </MainLayout>
          }
        />

        <Route
          path="/collection/:id"
          element={
            <div className="app">
              <main className="main-content" style={{ marginLeft: 0 }}>
                <FlashcardViewerRoute
                  collections={collections}
                  onUpdate={handleUpdateCollection}
                />
              </main>
            </div>
          }
        />

        <Route
          path="/recall-sheet/:id"
          element={
            <div className="app">
              <main className="main-content" style={{ marginLeft: 0 }}>
                <RecallViewerRoute
                  sheets={recallSheets}
                  onUpdate={handleUpdateRecallSheet}
                />
              </main>
            </div>
          }
        />

        <Route
          path="/map-viewer/:id"
          element={
            <div className="app">
              <main className="main-content" style={{ marginLeft: 0 }}>
                <MapViewerRoute
                  sessions={mapSessions}
                  onUpdate={handleUpdateMapSession}
                />
              </main>
            </div>
          }
        />
      </Routes>
    </>
  );
};

export default App;